import FormControl from '@mui/material/FormControl';
import { DatePicker, DateView } from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { ReactElement, useEffect, useState } from 'react';
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  useFormContext,
} from 'react-hook-form';

import CalendarIcon from '@/assets/icons/common/CalendarIcon.png';
import { useFormProviderRCPropsContext } from '@/components/form/FormProviderRC.comp.tsx';
import { isFormFieldRequired } from '@/utils/form/isFormFieldRequired.util.ts';

import { FormFieldLabel } from './FormFieldLabel.comp';
import { FormPropsModel } from './model/FormProps.model';
import { TextError } from './TextError.comp';

interface Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> extends FormPropsModel<TName> {
  disableFuture?: boolean;
  disablePast?: boolean;
  format?: string;
  isNotClearable?: boolean;
  maxDate?: Dayjs;
  minDate?: Dayjs;
  onChangeSideEffect?: (value: Dayjs | null) => void;
  openTo?: DateView;
  views?: DateView[];
}

const OpenPickerIcon = () => <img alt="Pick" src={CalendarIcon} />;

export const DateFormField = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>,
): ReactElement<Props<TFieldValues, TName>> => {
  const [cleared, setCleared] = useState(false);

  useEffect(() => {
    if (cleared) {
      const timeout = setTimeout(() => {
        setCleared(false);
      }, 1500);

      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [cleared]);

  const handleCleared = () => {
    setCleared(true);
  };

  const { control } = useFormContext<TFieldValues>();
  const {
    format,
    fullWidth,
    isNotClearable,
    label,
    name,
    onChangeSideEffect,
    placeholder,
    ...rest
  } = props;
  const { validation } = useFormProviderRCPropsContext();
  const render: ControllerProps<TFieldValues, TName>['render'] = ({
    field,
    fieldState: { error },
  }) => {
    const isRequired = isFormFieldRequired(field.name, validation);

    return (
      <FormControl fullWidth={fullWidth ?? true} sx={{ mt: 2 }}>
        <FormFieldLabel isRequired={isRequired} label={label} />
        <DatePicker
          {...field}
          {...rest}
          format={format ?? 'YYYY/MM/DD'}
          onChange={value => {
            if (onChangeSideEffect) {
              onChangeSideEffect(value);
            }
            field.onChange(value);
          }}
          slotProps={{
            clearButton: {
              style: { padding: '2px' },
            },
            field: { clearable: isNotClearable !== true, onClear: handleCleared },
            textField: {
              error: Boolean(error),
              fullWidth: true,
            },
          }}
          slots={{
            openPickerIcon: OpenPickerIcon,
          }}
          value={field.value ?? null}
        />
        <TextError error={error} />
      </FormControl>
    );
  };

  return <Controller<TFieldValues, TName> control={control} name={name} render={render} />;
};
